import { useState, useRef, useEffect } from "react";
import { X, Copy, Share2, Check, Sparkles } from "lucide-react";

/**
 * Generic share dialog — pre-writes a social-ready caption from a workout or program,
 * appends the coach's referral link, and offers copy / native-share.
 */
export default function ShareDialog({ open, onClose, kind, title, details, referralLink }) {
    const captions = {
        workout: `Just crushed "${title}" 💪\n${details || ""}\nBuilt with NDE Fitness — the workspace I use to coach. Try it here:\n${referralLink}`,
        program: `New program dropped: "${title}" — ${details || "multi-week block designed on NDE Fitness."}\nCoach smarter, not longer. Join me:\n${referralLink}`,
        progress: `${details || "Another week, another win."}\nTracked on NDE Fitness:\n${referralLink}`,
    };
    const [copied, setCopied] = useState(false);
    const [caption, setCaption] = useState(captions[kind] || captions.workout);
    const textareaRef = useRef(null);

    // Re-seed caption when a different share target opens
    useEffect(() => {
        if (open) setCaption(captions[kind] || captions.workout);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, kind, title, details, referralLink]);

    if (!open) return null;

    const copy = async () => {
        await navigator.clipboard.writeText(caption);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    const share = async () => {
        if (navigator.share) {
            try { await navigator.share({ title, text: caption, url: referralLink }); } catch {}
        } else { copy(); }
    };

    return (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={onClose} data-testid="share-dialog">
            <div className="bg-[var(--surface)] border border-[var(--border-bright)] w-full max-w-lg" onClick={(e) => e.stopPropagation()}>
                <div className="flex items-center justify-between p-5 border-b border-[var(--border)]">
                    <div className="flex items-center gap-3">
                        <Sparkles size={16} className="text-[var(--gold-bright)]" />
                        <h3 className="font-serif text-2xl">Share this {kind}</h3>
                    </div>
                    <button onClick={onClose}><X size={18} /></button>
                </div>
                <div className="p-5 space-y-4">
                    <p className="label-tiny">Caption · edit before posting</p>
                    <textarea
                        ref={textareaRef}
                        className="input-dark min-h-[160px] font-mono text-sm"
                        value={caption}
                        onChange={(e) => setCaption(e.target.value)}
                        data-testid="share-caption"
                    />
                    <div className="flex gap-2">
                        <button onClick={copy} className="btn-ghost inline-flex items-center gap-2 flex-1" data-testid="share-copy-btn">
                            {copied ? <Check size={14} className="text-[var(--success)]" /> : <Copy size={14} />}
                            {copied ? "Copied" : "Copy caption"}
                        </button>
                        <button onClick={share} className="btn-primary inline-flex items-center gap-2 flex-1" data-testid="share-native-btn">
                            <Share2 size={14} /> Share
                        </button>
                    </div>
                    <p className="text-[10px] text-[var(--text-faint)] font-mono">
                        Paste on IG, X, WhatsApp, Signal — your link is embedded, any signup credits toward your rewards.
                    </p>
                </div>
            </div>
        </div>
    );
}
